import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { ProfileContactV2, ProfileVariant, useTypedTranslation } from "@repo/ui";
import { type FC } from "react";
import { NotInMVPButton } from "../../../components/Drawer/NotInMVP";
import InformationList from "../../../components/InformationList";
import { InternalButtonLink, InternalLink } from "../../../components/Link";
import { Seo } from "../../../components/SEO";
import withProtection from "../../../highOrderComponents/withProtection";
import { isProfileV2Enabled, usePortalProfile, useProfile } from "../../../queryHooks";
import { EmailLink } from "../../../components/Profile/Profile.styled";

export const Title = "Kontaktní údaje";

const useTrackProfileChange = () => {
	const ai = useAppInsightsContext();
	return (changeText: string) => {
		ai.trackEvent({
			name: `[Change] ${changeText}`,
			properties: {
				type: "not in MVP",
			},
		});
	};
};

const ContactContent = ({ email, isLoading, mobile }: { email?: string | null; isLoading: boolean; mobile?: string | null }) => {
	const handleClick = useTrackProfileChange();
	const { t } = useTypedTranslation();

	return (
		<>
			<InformationList
				isLoading={isLoading}
				information={[
					{
						title: t("global.phone"),
						description: (
							<>
								{mobile ? (
									<InternalLink
										to={`tel:${mobile}`}
										key="dps-phone"
										variant="light"
									>
										{mobile}
									</InternalLink>
								) : (
									"-"
								)}
							</>
						),
					},
					{
						title: t("global.email"),
						description: (
							<>
								{email ? (
									<EmailLink
										to={`mailto:${email}`}
										key="dps-email"
										variant="light"
									>
										{email}
									</EmailLink>
								) : (
									"-"
								)}
							</>
						),
					},
				]}
			/>
			<NotInMVPButton
				text={t("profile.person.changeContactInfo")}
				onClick={() => {
					handleClick(t("profile.person.changeContactInfo"));
				}}
			/>
		</>
	);
};

const ContactV1: FC = () => {
	const result = useProfile();
	const { mobile, email } = result.data || {
		mobile: undefined,
		email: undefined,
	};

	return <ContactContent email={email} isLoading={result.isLoading} mobile={mobile} />;
};

const ContactV2: FC = () => {
	const result = usePortalProfile();
	const handleClick = useTrackProfileChange();
	const { t } = useTypedTranslation();

	return (
		<ProfileContactV2
			data={result.data}
			isLoading={result.isLoading}
			renderMobile={(value) => (
				<InternalLink to={`tel:${value}`} variant="light">
					{value}
				</InternalLink>
			)}
			renderEmail={(value) => (
				<EmailLink to={`mailto:${value}`} variant="light">
					{value}
				</EmailLink>
			)}
			action={
				<InternalButtonLink
					variant="text"
					size="small"
					to="/jsou-vase-udaje-spravne"
					href="/jsou-vase-udaje-spravne"
					onClick={() => {
						handleClick(t("profile.person.changeContactInfo"));
					}}
				>
					{t("global.change")}
				</InternalButtonLink>
			}
		/>
	);
};

const Contact: FC = () => {
	return <ProfileVariant isV2={isProfileV2Enabled()} v1={<ContactV1 />} v2={<ContactV2 />} />;
};

export default withProtection(Contact);

export const Head: FC = () => (
	<Seo title={Title}>
		<meta name="robots" content="noindex" />
	</Seo>
);
